import React, { useState, useEffect, Fragment } from 'react';
import { PlayIcon, TrashIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/solid';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import Masonry from 'react-masonry-css';
import { genericAPIs, userAPI } from '../../services/api';
import { UserVideo } from '../../types';
import AddVideoModal from './AddVideoModal';
import EditVideoModal from './EditVideoModal';
import GoogleDriveVideoEmbed from '../common/GoogleDriveVideoEmbed';

interface VideoSectionProps {
  userId: string;
  isOwnProfile: boolean;
  onSyncInstagram?: () => void;
}

// Masonry breakpoint configuration matching MasonryPortfolioGrid
const breakpointColumnsObj = {
  default: 5,
  1536: 4,
  1280: 3,
  1024: 3,
  768: 2,
  500: 3,
};

const VideoSection: React.FC<VideoSectionProps> = ({ userId, isOwnProfile, onSyncInstagram }) => {
  const [videos, setVideos] = useState<UserVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<UserVideo | null>(null);
  const [playingVideo, setPlayingVideo] = useState<UserVideo | null>(null);
  const [expandedTags, setExpandedTags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchVideos();
  }, [userId]);

  const fetchVideos = async () => {
    try {
      const response = await genericAPIs.getUserVideos(userId);
      setVideos(response.data || []);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (video: UserVideo, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingVideo(video);
  };

  const handleSaveEdit = async (
    videoId: string,
    updatedData: { title: string; description: string; tags: string[] }
  ) => {
    try {
      await genericAPIs.updateVideo(videoId, updatedData);
      toast.success('Video details updated!');
      fetchVideos();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update video details');
      throw error;
    }
  };

  const handleDelete = async (videoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await genericAPIs.deleteVideo(videoId);
      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  return (
    <div className="space-y-6">
      {isOwnProfile && (
        <div className="flex justify-end">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Video
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading videos...</div>
      ) : videos.length === 0 ? (
        <div className="text-center py-10 text-gray-500 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-800">
          {isOwnProfile ? "You haven't added any videos yet." : 'No videos to show.'}
        </div>
      ) : (
        <div className="w-full max-w-full min-w-0 overflow-hidden">
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="masonry-grid"
            columnClassName="masonry-grid_column"
          >
            {videos.map((video) => (
              <div
                key={video.id}
                className="group relative bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-800 mb-4"
                onClick={() => setPlayingVideo(video)}
              >
                <div className="relative bg-black overflow-hidden aspect-[4/5] sm:aspect-auto">
                  <img
                    src={
                      video.thumbnail_url ||
                      (video.platform === 'youtube'
                        ? `https://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`
                        : `https://drive.google.com/thumbnail?id=${video.video_id}&sz=w600`)
                    }
                    alt={video.title}
                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-105"
                    style={{
                      transform: video.rotation ? `rotate(${video.rotation}deg)` : undefined,
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-all">
                    <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20 group-hover:scale-110 transition-transform">
                      <PlayIcon className="h-6 w-6 text-white ml-0.5" />
                    </div>
                  </div>

                  {isOwnProfile && (
                    <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={(e) => handleEdit(video, e)}
                        className="p-1.5 bg-black/70 hover:bg-black text-white rounded-full backdrop-blur-md border border-white/20 transition hover:scale-105"
                        title="Edit Details"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(video.id, e)}
                        className="p-1.5 bg-red-600/90 hover:bg-red-600 text-white rounded-full backdrop-blur-md transition hover:scale-105"
                        title="Delete Video"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="hidden sm:block p-3">
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white line-clamp-1 mb-1">
                    {video.title}
                  </h3>
                  {video.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-1.5">
                      {video.description}
                    </p>
                  )}
                  {video.tags && video.tags.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800/80">
                      {!expandedTags[video.id] ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedTags((prev) => ({ ...prev, [video.id]: true }));
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          <span>Tags ({video.tags.length})</span>
                        </button>
                      ) : (
                        <div>
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {video.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="text-[10px] font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full border border-gray-200/50 dark:border-gray-700/50"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTags((prev) => ({ ...prev, [video.id]: false }));
                            }}
                            className="text-[10px] font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          >
                            Show Less
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Masonry>
        </div>
      )}

      <AddVideoModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={fetchVideos}
        onOpenSyncInstagram={onSyncInstagram}
      />

      <EditVideoModal
        isOpen={!!editingVideo}
        onClose={() => setEditingVideo(null)}
        video={editingVideo}
        onSave={handleSaveEdit}
      />

      {/* Video Player Modal */}
      <Transition appear show={!!playingVideo} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={() => setPlayingVideo(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-90" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-0 sm:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-black p-0 text-left align-middle shadow-xl transition-all">
                  {playingVideo && (
                    <div className="aspect-video w-full relative overflow-hidden bg-black rounded-xl">
                      {playingVideo.platform === 'youtube' ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${playingVideo.video_id}?autoplay=1`}
                          title={playingVideo.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute border-0 rounded-xl w-full h-full inset-0"
                        ></iframe>
                      ) : (
                        <GoogleDriveVideoEmbed
                          src={`https://drive.google.com/file/d/${playingVideo.video_id}/preview`}
                          title={playingVideo.title}
                        />
                      )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default VideoSection;
